import {
  fromPersistedTranscript,
  selectTranscriptAudioTuple,
  snapshotAudioTiming,
  toPersistedTranscript,
  transcriptFingerprint,
  type PersistedTranscriptInput,
  type PersistedTranscriptItem,
} from './persistence';

export interface TranscriptDeltaRequest {
  room_name: string;
  content: PersistedTranscriptItem[];
  base_revision: number;
  writer_id: string;
  writer_sequence: number;
}

export interface TranscriptDeltaResponse {
  success?: boolean;
  call_id?: string | null;
  revision: number;
  stale: boolean;
  applied?: unknown;
  segment_count?: number;
  /** Authoritative same-key conflicts returned when stale=true. */
  transcripts?: PersistedTranscriptInput[];
}

export interface TranscriptDeltaSendOptions {
  signal?: AbortSignal;
  keepalive: boolean;
}

export type SendTranscriptDelta = (
  request: TranscriptDeltaRequest,
  options: TranscriptDeltaSendOptions,
) => Promise<TranscriptDeltaResponse>;

export interface TranscriptFlushResult {
  ok: boolean;
  revision: number;
  writerSequence: number;
  pendingSegments: number;
  error?: Error;
}

export interface TranscriptDeltaWriterState {
  isSyncing: boolean;
  revision: number;
  pendingSegments: number;
  lastSyncTime: number | null;
  lastError: Error | null;
}

export interface SingleFlightDeltaWriterOptions {
  callId: string;
  writerId: string;
  send: SendTranscriptDelta;
  requestTimeoutMs?: number;
  keepaliveTimeoutMs?: number;
  maxBatchSegments?: number;
  maxBatchBytes?: number;
  maxKeepaliveBytes?: number;
  maxStaleRetries?: number;
  onStateChange?: (state: TranscriptDeltaWriterState) => void;
  onAuthoritativeSegments?: (segments: readonly PersistedTranscriptItem[]) => void;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
const DEFAULT_KEEPALIVE_TIMEOUT_MS = 10_000;
const DEFAULT_MAX_BATCH_SEGMENTS = 64;
const DEFAULT_MAX_BATCH_BYTES = 256 * 1024;
const DEFAULT_MAX_KEEPALIVE_BYTES = 48 * 1024;
const DEFAULT_MAX_STALE_RETRIES = 3;

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function normalizeRevision(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : fallback;
}

function normalizeServerSegment(
  value: PersistedTranscriptInput,
  index: number,
): PersistedTranscriptItem {
  return toPersistedTranscript(fromPersistedTranscript(value, index), index);
}

function safeLocalConflictWinner(
  local: PersistedTranscriptItem,
  server: PersistedTranscriptItem,
): boolean {
  if (local.is_final && !server.is_final) return true;
  if (!local.is_final && server.is_final) return false;

  return (
    local.segment_seq !== null &&
    server.segment_seq !== null &&
    local.segment_seq > server.segment_seq
  );
}

function serverHasMonotonicAuthority(
  server: PersistedTranscriptItem,
  local: PersistedTranscriptItem,
): boolean {
  if (server.is_final && !local.is_final) return true;
  if (!server.is_final && local.is_final) return false;
  if (server.segment_seq !== null && local.segment_seq === null) return true;
  return (
    server.segment_seq !== null &&
    local.segment_seq !== null &&
    server.segment_seq > local.segment_seq
  );
}

function mergeServerSemanticWithLocalMetadata(
  server: PersistedTranscriptItem,
  local: PersistedTranscriptItem,
): PersistedTranscriptItem {
  const audio = selectTranscriptAudioTuple(
    fromPersistedTranscript(server), fromPersistedTranscript(local), true,
    server.message === local.message && server.is_final === local.is_final && server.segment_seq === local.segment_seq,
  );
  return {
    ...server,
    speaker_name: local.speaker_name,
    timestamp_ms: local.timestamp_ms,
    participant_identity: audio.participantIdentity ?? null,
    is_self: local.is_self,
    is_officer: local.is_officer,
    color: local.color,
    initial: local.initial,
    segment_id: local.segment_id,
    segment_key: local.segment_key,
    bubble_id: local.bubble_id,
    audio_start_time: audio.audioStartTime ?? null,
    audio_end_time: audio.audioEndTime ?? null,
    ...(audio.audioTiming !== undefined ? { audio_timing: audio.audioTiming } : {}),
  };
}

function localWinnerWithAudioTuple(server: PersistedTranscriptItem, local: PersistedTranscriptItem): PersistedTranscriptItem {
  const audio = selectTranscriptAudioTuple(fromPersistedTranscript(server), fromPersistedTranscript(local));
  const rest = { ...local };
  delete rest.audio_timing;
  return {
    ...rest, sync_id: server.sync_id,
    participant_identity: audio.participantIdentity ?? null,
    audio_start_time: audio.audioStartTime ?? null,
    audio_end_time: audio.audioEndTime ?? null,
    ...(audio.audioTiming !== undefined ? { audio_timing: audio.audioTiming } : {}),
  };
}

function resolveChangedDesired(
  server: PersistedTranscriptItem,
  local: PersistedTranscriptItem,
  sent: PersistedTranscriptItem,
): PersistedTranscriptItem {
  if (safeLocalConflictWinner(local, server)) {
    return localWinnerWithAudioTuple(server, local);
  }
  if (serverHasMonotonicAuthority(server, local)) {
    return mergeServerSemanticWithLocalMetadata(server, local);
  }

  const equalSequence =
    (server.segment_seq === null && local.segment_seq === null) ||
    (server.segment_seq !== null && local.segment_seq === server.segment_seq);
  const semanticChangedAfterSend =
    local.message !== sent.message ||
    local.is_final !== sent.is_final;
  if (equalSequence && semanticChangedAfterSend) {
    return localWinnerWithAudioTuple(server, local);
  }
  // For metadata-only changes at equal/null sequence, retain the server's
  // semantic tuple and retry only the causally newer local metadata.
  if (equalSequence) {
    return mergeServerSemanticWithLocalMetadata(server, local);
  }
  return localWinnerWithAudioTuple(server, local);
}

interface ActiveBatchItem {
  syncId: string;
  segment: PersistedTranscriptItem;
  fingerprint: string;
}

/**
 * One-request-at-a-time, latest-only transcript delta writer.
 *
 * Redux may publish many interim versions while a request is active. The
 * pending map keeps only the newest object for each immutable sync ID. No
 * whole-history request or whole-history fingerprint is ever constructed.
 */
export class SingleFlightTranscriptDeltaWriter {
  private readonly callId: string;
  private readonly writerId: string;
  private readonly send: SendTranscriptDelta;
  private readonly requestTimeoutMs: number;
  private readonly keepaliveTimeoutMs: number;
  private readonly maxBatchSegments: number;
  private readonly maxBatchBytes: number;
  private readonly maxKeepaliveBytes: number;
  private readonly maxStaleRetries: number;
  private readonly onStateChange?: (state: TranscriptDeltaWriterState) => void;
  private readonly onAuthoritativeSegments?: (
    segments: readonly PersistedTranscriptItem[],
  ) => void;

  private revision = 0;
  private writerSequence = 0;
  private desiredBySyncId = new Map<string, PersistedTranscriptItem>();
  private acknowledgedBySyncId = new Map<
    string,
    { segment: PersistedTranscriptItem; fingerprint: string }
  >();
  private pendingSyncIds = new Set<string>();
  private drainPromise: Promise<void> | null = null;
  private preferKeepalive = false;
  private lastSyncTime: number | null = null;
  private lastError: Error | null = null;
  private disposed = false;
  private activeAbortController: AbortController | null = null;
  private emergencyKeepalivePromise: Promise<void> | null = null;
  private emergencyKeepaliveSyncIds = new Set<string>();

  constructor(options: SingleFlightDeltaWriterOptions) {
    this.callId = options.callId;
    this.writerId = options.writerId;
    this.send = options.send;
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    this.keepaliveTimeoutMs = options.keepaliveTimeoutMs ?? DEFAULT_KEEPALIVE_TIMEOUT_MS;
    this.maxBatchSegments = options.maxBatchSegments ?? DEFAULT_MAX_BATCH_SEGMENTS;
    this.maxBatchBytes = options.maxBatchBytes ?? DEFAULT_MAX_BATCH_BYTES;
    this.maxKeepaliveBytes = options.maxKeepaliveBytes ?? DEFAULT_MAX_KEEPALIVE_BYTES;
    this.maxStaleRetries = options.maxStaleRetries ?? DEFAULT_MAX_STALE_RETRIES;
    this.onStateChange = options.onStateChange;
    this.onAuthoritativeSegments = options.onAuthoritativeSegments;
    this.emitState();
  }

  /** Seed only the server-persisted checkpoint returned by hydration. */
  seedCheckpoint(revision: number, segments: readonly PersistedTranscriptInput[]): void {
    if (this.disposed) return;

    this.revision = normalizeRevision(revision, this.revision);
    this.acknowledgedBySyncId.clear();
    segments.forEach((raw, index) => {
      const segment = normalizeServerSegment(raw, index);
      this.acknowledgedBySyncId.set(segment.sync_id, {
        segment,
        fingerprint: transcriptFingerprint(segment),
      });
    });
    this.recomputePending();
    this.emitState();
  }

  /** Queue desired canonical segments; unchanged checkpointed fields are skipped. */
  enqueue(
    segments: readonly PersistedTranscriptItem[],
    options: { defer?: boolean } = {},
  ): void {
    if (this.disposed) return;

    for (const segment of segments) {
      const snapshot = {
        ...segment,
        ...(segment.audio_timing !== undefined ? { audio_timing: snapshotAudioTiming(segment.audio_timing) } : {}),
      };
      const syncId = snapshot.sync_id;
      const fingerprint = transcriptFingerprint(snapshot);
      this.desiredBySyncId.set(syncId, snapshot);
      if (this.acknowledgedBySyncId.get(syncId)?.fingerprint === fingerprint) {
        this.pendingSyncIds.delete(syncId);
      } else {
        this.pendingSyncIds.add(syncId);
      }
    }

    this.emitState();
    if (!options.defer) {
      void this.ensureDrain().catch(() => {
        // State/error are retained for an explicit flush or a future enqueue.
      });
    }
  }

  async flush(options: { force?: boolean; keepalive?: boolean } = {}): Promise<TranscriptFlushResult> {
    if (this.disposed) {
      return this.flushResult(new Error('Transcript delta writer is disposed'));
    }

    if (options.force) {
      for (const syncId of this.desiredBySyncId.keys()) {
        this.pendingSyncIds.add(syncId);
      }
    }
    if (options.keepalive) {
      this.preferKeepalive = true;
      if (this.activeAbortController) {
        // The active item was removed from pending when its normal request
        // started. Recompute from desired-vs-ack so even an unchanged active
        // version is represented in the emergency shadow.
        this.recomputePending();
        // Start the bounded shadow synchronously, before abort rejection runs
        // in a later microtask. Atomic server merge makes the brief ambiguity
        // safe, while guaranteeing pagehide actually initiates a keepalive.
        const emergency = this.startEmergencyKeepalive();
        if (!emergency) {
          // A single oversized segment cannot safely use browser keepalive.
          // Leave the normal request running instead of aborting the only copy.
          return this.flushResult(this.lastError ?? undefined);
        }
        this.activeAbortController.abort();
        try {
          await emergency;
        } catch (error) {
          this.lastError = error instanceof Error ? error : new Error(String(error));
        }
        const result = this.flushResult(this.lastError ?? undefined);
        return {
          ...result,
          ok: !this.lastError && this.pendingSyncIds.size === 0,
        };
      }
    }

    this.lastError = null;
    try {
      await this.ensureDrain();
    } catch (error) {
      this.lastError = error instanceof Error ? error : new Error(String(error));
    }

    return this.flushResult(this.lastError ?? undefined);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    // A normal fetch is locally controllable and must not survive disposal.
    // Keepalive sends deliberately have no controller; hook finalization waits
    // for those before calling dispose(). Late keepalive responses are ignored
    // by the disposed checks in drain().
    this.activeAbortController?.abort();
    this.activeAbortController = null;
    this.pendingSyncIds.clear();
    this.desiredBySyncId.clear();
    this.emitState();
  }

  getState(): TranscriptDeltaWriterState {
    return {
      isSyncing: !this.disposed && (
        this.drainPromise !== null || this.emergencyKeepalivePromise !== null
      ),
      revision: this.revision,
      pendingSegments: this.pendingSyncIds.size,
      lastSyncTime: this.lastSyncTime,
      lastError: this.lastError,
    };
  }

  private flushResult(error?: Error): TranscriptFlushResult {
    const pendingSegments = this.pendingSyncIds.size;
    return {
      ok: !error && pendingSegments === 0 && this.drainPromise === null,
      revision: this.revision,
      writerSequence: this.writerSequence,
      pendingSegments,
      ...(error ? { error } : {}),
    };
  }

  private recomputePending(): void {
    this.pendingSyncIds.clear();
    for (const [syncId, desired] of this.desiredBySyncId) {
      if (
        this.acknowledgedBySyncId.get(syncId)?.fingerprint !==
        transcriptFingerprint(desired)
      ) {
        this.pendingSyncIds.add(syncId);
      }
    }
  }

  private ensureDrain(): Promise<void> {
    if (this.drainPromise) return this.drainPromise;
    if (this.pendingSyncIds.size === 0) return Promise.resolve();

    this.drainPromise = this.drain()
      .finally(() => {
        this.drainPromise = null;
        this.preferKeepalive = false;
        this.emitState();
      });
    this.emitState();
    return this.drainPromise;
  }

  private selectBatch(keepalive: boolean): ActiveBatchItem[] {
    const byteBudget = keepalive ? this.maxKeepaliveBytes : this.maxBatchBytes;
    const batch: ActiveBatchItem[] = [];
    let usedBytes = 256; // envelope/revision/writer metadata allowance

    for (const syncId of this.pendingSyncIds) {
      const segment = this.desiredBySyncId.get(syncId);
      if (!segment) {
        this.pendingSyncIds.delete(syncId);
        continue;
      }

      const fingerprint = transcriptFingerprint(segment);
      const segmentBytes = byteLength(fingerprint) + 1;
      if (batch.length > 0 && (
        batch.length >= this.maxBatchSegments ||
        usedBytes + segmentBytes > byteBudget
      )) {
        break;
      }
      if (batch.length === 0 && usedBytes + segmentBytes > byteBudget) {
        if (keepalive) return [];
      }

      batch.push({ syncId, segment, fingerprint });
      usedBytes += segmentBytes;
      this.pendingSyncIds.delete(syncId);
      if (batch.length >= this.maxBatchSegments) break;
    }

    return batch;
  }

  private async drain(): Promise<void> {
    let consecutiveReconcileRetries = 0;

    while (!this.disposed && this.pendingSyncIds.size > 0) {
      const keepalive = this.preferKeepalive;
      const batch = this.selectBatch(keepalive);
      if (batch.length === 0) {
        const error = new Error('Pending transcript delta exceeds the keepalive byte budget');
        this.lastError = error;
        throw error;
      }

      this.writerSequence += 1;
      const request: TranscriptDeltaRequest = {
        room_name: this.callId,
        content: batch.map((entry) => entry.segment),
        base_revision: this.revision,
        writer_id: this.writerId,
        writer_sequence: this.writerSequence,
      };

      let timeout: ReturnType<typeof setTimeout> | null = null;
      const controller = new AbortController();
      this.activeAbortController = keepalive ? null : controller;
      const timeoutMs = keepalive ? this.keepaliveTimeoutMs : this.requestTimeoutMs;
      if (timeoutMs > 0) {
        timeout = setTimeout(() => controller.abort(), timeoutMs);
      }

      let response: TranscriptDeltaResponse;
      try {
        response = await this.send(request, {
          signal: controller.signal,
          keepalive,
        });
      } catch (error) {
        const normalized = error instanceof Error ? error : new Error(String(error));
        const preemptedForKeepalive = Boolean(
          !this.disposed &&
          this.preferKeepalive &&
          !keepalive &&
          controller.signal.aborted,
        );
        if (!this.disposed) {
          this.requeueBatch(batch);
          if (!preemptedForKeepalive) this.lastError = normalized;
        }
        if (preemptedForKeepalive) continue;
        throw normalized;
      } finally {
        if (timeout) clearTimeout(timeout);
        if (this.activeAbortController === controller) {
          this.activeAbortController = null;
        }
      }

      if (this.disposed) return;

      this.revision = Math.max(
        this.revision,
        normalizeRevision(response.revision, this.revision),
      );
      if (Array.isArray(response.transcripts)) {
        const missing = this.reconcileAuthoritativeBatch(batch, response.transcripts);
        if (missing > 0) {
          consecutiveReconcileRetries += 1;
          if (consecutiveReconcileRetries > this.maxStaleRetries) {
            const error = new Error(
              'Transcript delta response omitted requested segments after bounded retries',
            );
            this.lastError = error;
            throw error;
          }
        } else {
          consecutiveReconcileRetries = 0;
        }
        this.lastError = null;
        this.lastSyncTime = Date.now();
        this.emitState();
        continue;
      }
      if (response.stale) {
        consecutiveReconcileRetries += 1;
        this.requeueBatch(batch);
        if (consecutiveReconcileRetries > this.maxStaleRetries) {
          const error = new Error('Transcript delta remained stale after bounded retries');
          this.lastError = error;
          throw error;
        }
        continue;
      }

      consecutiveReconcileRetries = 0;
      for (const entry of batch) {
        this.acknowledgedBySyncId.set(entry.syncId, {
          segment: entry.segment,
          fingerprint: entry.fingerprint,
        });
        const desired = this.desiredBySyncId.get(entry.syncId);
        if (desired && transcriptFingerprint(desired) !== entry.fingerprint) {
          this.pendingSyncIds.add(entry.syncId);
        } else {
          // A flush may have re-enqueued the exact in-flight version. Once the
          // server acknowledges it, that duplicate pending marker is satisfied.
          this.pendingSyncIds.delete(entry.syncId);
        }
      }
      this.lastError = null;
      this.lastSyncTime = Date.now();
      this.emitState();
    }
  }

  private requeueBatch(batch: readonly ActiveBatchItem[]): void {
    for (const entry of batch) {
      const desired = this.desiredBySyncId.get(entry.syncId);
      if (
        desired &&
        this.acknowledgedBySyncId.get(entry.syncId)?.fingerprint !==
          transcriptFingerprint(desired) &&
        !this.emergencyKeepaliveSyncIds.has(entry.syncId)
      ) {
        this.pendingSyncIds.add(entry.syncId);
      }
    }
  }

  private startEmergencyKeepalive(): Promise<void> | null {
    if (this.emergencyKeepalivePromise) return this.emergencyKeepalivePromise;
    const batch = this.selectBatch(true);
    if (batch.length === 0) {
      this.lastError = new Error(
        'Pending transcript delta exceeds the emergency keepalive byte budget',
      );
      return null;
    }

    this.writerSequence += 1;
    const request: TranscriptDeltaRequest = {
      room_name: this.callId,
      content: batch.map((entry) => entry.segment),
      base_revision: this.revision,
      writer_id: this.writerId,
      writer_sequence: this.writerSequence,
    };
    this.emergencyKeepaliveSyncIds = new Set(batch.map((entry) => entry.syncId));

    const controller = new AbortController();
    const timeout = this.keepaliveTimeoutMs > 0
      ? setTimeout(() => controller.abort(), this.keepaliveTimeoutMs)
      : null;
    const emergency = (async () => {
      try {
        const response = await this.send(request, {
          keepalive: true,
          signal: controller.signal,
        });
        if (this.disposed) return;
        this.revision = Math.max(
          this.revision,
          normalizeRevision(response.revision, this.revision),
        );
        if (Array.isArray(response.transcripts)) {
          this.reconcileAuthoritativeBatch(batch, response.transcripts);
          this.lastError = null;
          this.lastSyncTime = Date.now();
          return;
        }
        if (response.stale) {
          this.requeueBatch(batch);
          return;
        }

        for (const entry of batch) {
          this.acknowledgedBySyncId.set(entry.syncId, {
            segment: entry.segment,
            fingerprint: entry.fingerprint,
          });
          const desired = this.desiredBySyncId.get(entry.syncId);
          if (desired && transcriptFingerprint(desired) !== entry.fingerprint) {
            this.pendingSyncIds.add(entry.syncId);
          } else {
            this.pendingSyncIds.delete(entry.syncId);
          }
        }
        this.lastError = null;
        this.lastSyncTime = Date.now();
      } catch (error) {
        if (!this.disposed) {
          for (const entry of batch) {
            if (this.desiredBySyncId.has(entry.syncId)) {
              this.pendingSyncIds.add(entry.syncId);
            }
          }
        }
        throw error;
      } finally {
        if (timeout) clearTimeout(timeout);
      }
    })();
    this.emergencyKeepalivePromise = emergency.finally(() => {
      this.emergencyKeepalivePromise = null;
      this.emergencyKeepaliveSyncIds.clear();
      this.emitState();
    });
    this.emitState();
    return this.emergencyKeepalivePromise;
  }

  private reconcileAuthoritativeBatch(
    batch: readonly ActiveBatchItem[],
    authoritative: readonly PersistedTranscriptInput[],
  ): number {
    const returnedIds = new Set<string>();
    const sentBySyncId = new Map(batch.map((entry) => [entry.syncId, entry]));
    const reduxProjection: PersistedTranscriptItem[] = [];
    authoritative.forEach((raw, index) => {
      const server = normalizeServerSegment(raw, index);
      returnedIds.add(server.sync_id);
      this.acknowledgedBySyncId.set(server.sync_id, {
        segment: server,
        fingerprint: transcriptFingerprint(server),
      });

      const local = this.desiredBySyncId.get(server.sync_id);
      const sent = sentBySyncId.get(server.sync_id);
      const changedAfterSend = Boolean(
        local &&
        sent &&
        transcriptFingerprint(local) !== sent.fingerprint,
      );
      const resolved = local
        ? changedAfterSend
          ? resolveChangedDesired(server, local, sent!.segment)
          : safeLocalConflictWinner(local, server)
            ? localWinnerWithAudioTuple(server, local)
            : server
        : server;
      this.desiredBySyncId.set(server.sync_id, resolved);
      if (transcriptFingerprint(resolved) !== transcriptFingerprint(server)) {
        this.pendingSyncIds.add(server.sync_id);
      } else {
        this.pendingSyncIds.delete(server.sync_id);
      }
      if (!local || transcriptFingerprint(resolved) !== transcriptFingerprint(local)) {
        reduxProjection.push(resolved);
      }
    });

    let missing = 0;
    for (const entry of batch) {
      if (!returnedIds.has(entry.syncId) && this.desiredBySyncId.has(entry.syncId)) {
        missing += 1;
        this.pendingSyncIds.add(entry.syncId);
      }
    }
    if (reduxProjection.length > 0) {
      this.onAuthoritativeSegments?.(reduxProjection);
    }
    return missing;
  }

  private emitState(): void {
    this.onStateChange?.(this.getState());
  }
}

type RegisteredTranscriptSync = {
  token: symbol;
  flush: (options?: { force?: boolean; keepalive?: boolean }) => Promise<TranscriptFlushResult>;
};

const activeTranscriptSyncs = new Map<string, RegisteredTranscriptSync>();

export function registerTranscriptSync(
  callId: string,
  flush: RegisteredTranscriptSync['flush'],
): () => void {
  const token = Symbol(callId);
  activeTranscriptSyncs.set(callId, { token, flush });
  return () => {
    if (activeTranscriptSyncs.get(callId)?.token === token) {
      activeTranscriptSyncs.delete(callId);
    }
  };
}

/** Browser-local finalization bridge used by archive producers. */
export async function flushTranscriptSync(
  callId: string,
): Promise<TranscriptFlushResult | null> {
  return activeTranscriptSyncs.get(callId)?.flush() ?? null;
}
